import { api } from "./api";
import type { DisconnectParams } from "../components/marketplace/DisconnectMarketplaceDialog";
import type {
  AgentInvitePreviewResponse,
  CurrentUserResponse,
  LegalAcceptanceRequest,
  LegalAcceptanceResponse,
  LinkAgentInviteRequest,
  OnboardingRequest,
  OnboardingResponse,
} from "./api-types";
import type {
  AgentSharingLevel,
  FinancialSourceFamily,
  FinancialVerificationTier,
  OnboardingData,
} from "./onboarding-types";

/**
 * Maps frontend OnboardingData to the API request shape.
 */
export function toOnboardingRequest(data: OnboardingData): OnboardingRequest {
  return {
    buyer_type: data.buyerType!,
    target_area: data.targetArea!,
    ...(data.targetPlaceId ? { place_id: data.targetPlaceId } : {}),
    household_income: data.householdIncome!,
    employment_months: data.employmentMonths,
    recent_job_gap: data.recentJobGap,
    has_second_job: data.hasSecondJob,
    is_self_employed: data.isSelfEmployed,
    has_bonus_income: data.hasBonusIncome,
    has_commission_income: data.hasCommissionIncome,
    savings: data.savings!,
    credit_score: data.creditScore!,
    monthly_debts: data.monthlyDebts!,
    target_price: data.budget!, // frontend "budget" = backend "target_price"
    monthly_budget: data.monthlyBudget,
    timeline: data.timeline!,
    housing_situation: data.housingSituation!,
    lease_expiration:
      data.housingSituation === "renting" ? data.leaseExpiration : null,
    first_name: data.firstName,
    agent_id: data.agentId,
    agent_invite_code: data.agentInviteCode,
    manual_invite_code: data.manualInviteCode,
    email: data.email,
  };
}

export async function submitOnboarding(
  data: OnboardingData,
  token: string,
): Promise<OnboardingResponse> {
  const req = toOnboardingRequest(data);
  return api.post<OnboardingResponse>("/api/onboarding", req, token);
}

export async function fetchCurrentUser(
  token: string,
): Promise<CurrentUserResponse> {
  return api.get<CurrentUserResponse>("/api/users/me", token);
}

export async function acceptLegal(
  req: LegalAcceptanceRequest,
  token: string,
): Promise<LegalAcceptanceResponse> {
  return api.post<LegalAcceptanceResponse>(
    "/api/users/me/legal-acceptance",
    req,
    token,
  );
}

export async function previewAgentInvite(
  inviteCode: string,
  token: string,
): Promise<AgentInvitePreviewResponse> {
  const params = new URLSearchParams({ code: inviteCode });
  return api.get<AgentInvitePreviewResponse>(
    `/api/onboarding/agent-invite?${params.toString()}`,
    token,
  );
}

export async function linkAgentInvite(
  inviteCode: string,
  sharingLevel: AgentSharingLevel,
  token: string,
): Promise<AgentInvitePreviewResponse> {
  const req: LinkAgentInviteRequest = {
    invite_code: inviteCode,
    sharing_level: sharingLevel,
  };

  return api.post<AgentInvitePreviewResponse>(
    "/api/onboarding/agent-link",
    req,
    token,
  );
}

export interface ProCodeResolveResponse {
  found: boolean;
  code?: string;
  name?: string;
  brokerage?: string;
  avatar?: string | null;
  role?: string;
}

export async function resolveProCode(
  inviteCode: string,
): Promise<ProCodeResolveResponse> {
  return api.post<ProCodeResolveResponse>("/api/public/pro-codes/resolve", {
    code: inviteCode,
  });
}

export async function updateConsumerProLinkSharePref(
  linkId: string,
  shareWithPro: boolean,
  token: string,
) {
  return api.patch<AgentInvitePreviewResponse>(
    `/api/consumer-pro-links/${linkId}/share-pref`,
    { share_with_pro: shareWithPro },
    token,
  );
}

// updateConsumerProLinkSharingLevel changes the FINANCIAL sharing axis
// (status_only ↔ full_financial) on a link the consumer owns. This is distinct
// from updateConsumerProLinkSharePref, which controls the ACTIVITY axis. The
// backend restricts the value to the two financial tiers ("none" is reached by
// removing the link).
export async function updateConsumerProLinkSharingLevel(
  linkId: string,
  sharingLevel: AgentSharingLevel,
  token: string,
) {
  return api.patch<AgentInvitePreviewResponse>(
    `/api/consumer-pro-links/${linkId}/sharing-level`,
    { sharing_level: sharingLevel },
    token,
  );
}

/**
 * Why the buyer removed a pro. The server records it when the removal is a
 * marketplace disconnect (a pro reached through the marketplace), and
 * defaults it to "other" when omitted.
 */
export type RemoveProReason = Pick<DisconnectParams, "reason" | "note">;

/**
 * Removes a pro from the buyer's team. For a pro the buyer is connected to
 * through the marketplace this IS the marketplace disconnect — the server ends
 * the connection, stops its check-ins, and applies the 24h refund guarantee —
 * so the response is the disconnect outcome; otherwise it is empty (204).
 */
export async function deleteConsumerProLink(
  linkId: string,
  token: string,
  removal?: RemoveProReason,
): Promise<void> {
  await api.delete<unknown>(`/api/consumer-pro-links/${linkId}`, token, removal);
}

export interface ProAdmissionServiceAreaOption {
  value: string;
  label: string;
}

export interface ProAdmissionServiceAreasResponse {
  service_areas: ProAdmissionServiceAreaOption[];
  /**
   * Always empty. The service-area field takes free text that the server
   * resolves, so there is no alias table to consult; the key stays on the wire
   * only so older clients keep parsing the response.
   */
  aliases?: Record<string, string>;
}

// Launched cities, derived server-side from the active metros' ZIPs. They are
// rendered as SUGGESTIONS for the free-text service-area input, never as a
// closed list: admission is decided by the server resolving whatever is typed.
export async function getProAdmissionServiceAreas(): Promise<ProAdmissionServiceAreasResponse> {
  return api.get<ProAdmissionServiceAreasResponse>(
    "/api/public/pro-admission/service-areas",
  );
}

export type ProAdmissionProType = "agent" | "lo";

export interface ProAdmissionWelcomePreviewResponse {
  redemption_id: string;
  status: string;
  program_slug: string;
  program_name: string;
  pro_type: ProAdmissionProType;
}

export interface ProAdmissionWelcomeResponse extends ProAdmissionWelcomePreviewResponse {
  email: string;
  name: string;
  invite_code: string;
  referral_url: string;
  profile_ready: boolean;
}

export async function previewProAdmissionWelcome(
  redemptionId: string,
): Promise<ProAdmissionWelcomePreviewResponse> {
  return api.get<ProAdmissionWelcomePreviewResponse>(
    `/api/public/pro-admission/welcome/${encodeURIComponent(redemptionId)}`,
  );
}

export async function completeProAdmissionWelcome(
  redemptionId: string,
  token: string,
): Promise<ProAdmissionWelcomeResponse> {
  return api.get<ProAdmissionWelcomeResponse>(
    `/api/pro-admission/welcome/${encodeURIComponent(redemptionId)}`,
    token,
  );
}

// ─── Manual financial entries ─────────────────────────────────────────────────

export type ManualEntryKind = "liability" | "income" | "asset";
export type LiabilityKind =
  | "auto"
  | "personal"
  | "student"
  | "mortgage"
  | "heloc"
  | "bnpl"
  | "other";
export type ManualIncomeType =
  | "self_employment"
  | "employment"
  | "rental"
  | "other";
export type IncomeFrequency = "monthly" | "annual";
export type AssetKind =
  | "checking"
  | "savings"
  | "investment"
  | "retirement"
  | "other";

export interface ManualFinancialEntry {
  id: string;
  profile_id: string;
  entry_kind: ManualEntryKind;
  /** 'manual' = user-entered; 'document' and 'plaid' are read-only. */
  source: FinancialSourceFamily;
  source_family?: FinancialSourceFamily;
  verification_tier?: FinancialVerificationTier;
  editable?: boolean;
  /** Set when source = 'document'; the document_uploads.id that produced this row. */
  document_id?: string;
  institution_name?: string;
  plaid_account_id?: string;
  display_name: string;
  liability_kind?: LiabilityKind;
  monthly_payment_cents?: number;
  current_balance_cents?: number;
  apr_bps?: number;
  originator?: string;
  income_type?: ManualIncomeType;
  income_frequency?: IncomeFrequency;
  income_amount_cents?: number;
  asset_kind?: AssetKind;
  asset_value_cents?: number;
  custodian?: string;
  created_at: string;
  updated_at: string;
}

export interface ManualEntryRequest {
  entry_kind: ManualEntryKind;
  display_name: string;
  liability_kind?: LiabilityKind;
  monthly_payment_cents?: number;
  current_balance_cents?: number;
  apr_bps?: number;
  originator?: string;
  income_type?: ManualIncomeType;
  income_frequency?: IncomeFrequency;
  income_amount_cents?: number;
  asset_kind?: AssetKind;
  asset_value_cents?: number;
  custodian?: string;
}

interface ManualEntriesResponse {
  entries: ManualFinancialEntry[];
  contributors?: ManualFinancialEntry[];
}

export async function listManualEntries(
  token: string,
): Promise<ManualFinancialEntry[]> {
  const res = await api.get<ManualEntriesResponse>(
    "/api/financials/manual-entries",
    token,
  );
  return (res.contributors ?? res.entries ?? []).map(normalizeContributor);
}

// ManualEntryMutationResponse mirrors the SILL-302 P1.1 envelope shape
// returned by POST/PUT/DELETE /api/financials/manual-entries. The handler
// rebuilds the resolved snapshot synchronously and packages the freshly
// projected profile alongside the mutated entry so callers can update
// dashboard / readiness state from this body instead of refetching.
//
// Only `entry` is consumed today (the dashboard refresh callback continues
// to refetch /api/users/me, which now sees the advanced snapshot pointer
// because the rebuild is synchronous). The other fields are typed as
// `unknown` to document their presence without coupling the client to
// the server's resolved-profile schema — a follow-up can consume them
// directly to skip the round trip.
interface ManualEntryMutationResponse {
  entry?: ManualFinancialEntry;
  profile?: unknown;
  snapshot?: unknown;
  financials?: unknown;
  readiness?: unknown;
}

export async function createManualEntry(
  req: ManualEntryRequest,
  token: string,
): Promise<ManualFinancialEntry> {
  const res = await api.post<ManualEntryMutationResponse>(
    "/api/financials/manual-entries",
    req,
    token,
  );
  if (!res.entry) {
    throw new Error("create manual entry: missing entry in response");
  }
  return normalizeContributor(res.entry);
}

export async function updateManualEntry(
  id: string,
  req: Omit<ManualEntryRequest, "entry_kind">,
  token: string,
): Promise<ManualFinancialEntry> {
  const res = await api.put<ManualEntryMutationResponse>(
    `/api/financials/manual-entries/${encodeURIComponent(id)}`,
    req,
    token,
  );
  if (!res.entry) {
    throw new Error("update manual entry: missing entry in response");
  }
  return normalizeContributor(res.entry);
}

export async function deleteManualEntry(
  id: string,
  token: string,
): Promise<void> {
  // SILL-302 P1.1: DELETE now returns 200 + envelope (profile/snapshot/
  // financials/readiness) instead of 204 No Content. The api.delete
  // helper handles both status codes; the envelope body is ignored here
  // because the hook only needs to drop the entry from local state.
  await api.delete<ManualEntryMutationResponse>(
    `/api/financials/manual-entries/${encodeURIComponent(id)}`,
    token,
  );
}

function normalizeContributor(
  entry: ManualFinancialEntry,
): ManualFinancialEntry {
  const sourceFamily = entry.source_family ?? entry.source ?? "manual";
  return {
    ...entry,
    source: sourceFamily,
    source_family: sourceFamily,
    verification_tier:
      entry.verification_tier ?? verificationTierForSource(sourceFamily),
    editable: entry.editable ?? sourceFamily === "manual",
  };
}

function verificationTierForSource(
  sourceFamily: FinancialSourceFamily,
): FinancialVerificationTier {
  switch (sourceFamily) {
    case "document":
      return "document";
    case "plaid":
      return "connected";
    case "manual":
    default:
      return "self_reported";
  }
}
